import { getLogger } from 'app-components/utils/core';

import { makeAiApiRequest } from '../../ai-insights/aiInsightsSlice';
import type { AiPromptRequestData } from '../../ai-insights/aiInsightsTypes';
import type { TransactionActionExecutionProfile } from './proposalTypes';

const USER_FACING_STRUCTURED_MODEL_ERROR = 'We could not generate suggestions right now. Please try again.';
const log = getLogger('flux/ai-chat/transaction-actions/executeStructuredModelRequest');

const withTimeout = <T>(promise: Promise<T>, timeoutMs: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Transaction action request timed out')), timeoutMs);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });

type StructuredModelExecutionResult<T> = { status: 'success'; data: T } | { status: 'request_failed'; error: string };

type ExecuteStructuredModelRequestArgs<T> = {
  request: AiPromptRequestData;
  profile: TransactionActionExecutionProfile;
  capability: string;
  parseResponse: (assistantResponse: string) => T | null;
};

// Shared transport policy for transaction proposals and metric-based insights.
export const executeStructuredModelRequest = async <T>({
  request,
  profile,
  capability,
  parseResponse,
}: ExecuteStructuredModelRequestArgs<T>): Promise<StructuredModelExecutionResult<T>> => {
  const attempt = async (attemptRequest: AiPromptRequestData): Promise<StructuredModelExecutionResult<T>> => {
    try {
      const response = await withTimeout(makeAiApiRequest(attemptRequest), profile.timeoutMs);
      const data = parseResponse(response.assistantResponse);
      return data !== null
        ? { status: 'success', data }
        : { status: 'request_failed', error: USER_FACING_STRUCTURED_MODEL_ERROR };
    } catch (error) {
      log.error(`Structured model request failed for capability: ${capability}`, error);
      return { status: 'request_failed', error: USER_FACING_STRUCTURED_MODEL_ERROR };
    }
  };

  const firstAttempt = await attempt(request);
  if (firstAttempt.status === 'success' || !profile.retryModel) return firstAttempt;

  return attempt({
    ...request,
    options: { ...request.options, model: profile.retryModel },
  });
};
